import { Link } from '@tanstack/react-router';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import {
	type ConsentimientoCookies,
	guardarConsentimientoCookies,
	leerConsentimientoCookies,
} from '../consentimientoCookies';

/**
 * Aviso de cookies (P-01, `docs/legal/politica-de-cookies.md` § 4): «al mismo nivel y con la
 * misma facilidad, aceptar o rechazar» — el cliente lo exige, así que `cookies.aceptar` y
 * `cookies.rechazar` son dos `Button` reales, del mismo tamaño, uno junto al otro; ninguno es un
 * enlace de texto subordinado al otro. «Seguir navegando no es consentir»: sin pulsar uno de los
 * dos, el aviso no se cierra solo, y `useState` se inicializa leyendo `localStorage`
 * (`consentimientoCookies.ts`) para no mostrarlo de nuevo si ya hay una decisión guardada.
 *
 * Mismo criterio de layout que `AvisoModoVisitante` (H-06/H-26/H-33/T-31, ver su JSDoc en
 * `features/auth/components/avisoModoVisitante.tsx`): siempre en flujo, `static`, nunca
 * `fixed`/`sticky` — una franja anclada a un borde tapa contenido en viewports bajos, la misma
 * lección ya pagada tres veces en este repositorio.
 *
 * **Nombre accesible compuesto, sin texto nuevo.** Este aviso se monta en `__root.tsx`, en TODAS
 * las rutas — también en `/centro`, que ya tiene sus propios botones «Aceptar»/«Rechazar»/
 * «Deshacer» para las solicitudes (`moderation:acciones.*`, casualmente los mismos literales que
 * `legal:cookies.aceptar`/`rechazar`). Dos botones con el mismo nombre accesible «Aceptar» en la
 * misma página confunden a quien navega con lector de pantalla (¿cuál de los dos activa?) y, en
 * la práctica, rompían `tests/centroRuta.test.tsx` (ajeno a esta tarea, `getByRole('button',
 * {name: 'Aceptar'})` sin acotar encontraba el de cookies antes de que el panel de moderación
 * terminase de cargar el suyo). La correción NO inventa texto —«no inventes texto de cara al
 * público» sigue aplicando también al nombre accesible— sino que compone el nombre de cada botón
 * con `aria-labelledby` a partir de DOS textos que YA están en el DOM: el titular del aviso
 * (`cookies.titulo`, el `<p id={idTitulo}>` de abajo) y la propia etiqueta visible del botón
 * (`cookies.aceptar`/`cookies.rechazar`, referenciándose a sí mismo). El resultado, «Tu
 * privacidad, tus reglas Aceptar», es distinto de un «Aceptar» suelto sin necesitar ninguna clave
 * i18n nueva ni ninguna cadena hardcodeada.
 */
export const AvisoCookies = (): React.JSX.Element | null => {
	const { t } = useTranslation('legal');
	const [consentimiento, setConsentimiento] = useState<ConsentimientoCookies | null>(() => leerConsentimientoCookies());
	const idTitulo = useId();
	const idBotonRechazar = useId();
	const idBotonAceptar = useId();

	if (consentimiento !== null) {
		return null;
	}

	const decidir = (valor: ConsentimientoCookies): void => {
		guardarConsentimientoCookies(valor);
		setConsentimiento(valor);
	};

	return (
		<div className='mx-auto max-w-3xl px-4 sm:px-6'>
			<section
				aria-labelledby={idTitulo}
				className='static my-2 flex flex-col gap-3 rounded-lg border border-border bg-paper-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'
			>
				<div>
					<p className='font-black text-ink' id={idTitulo}>
						{t('cookies.titulo')}
					</p>
					<p className='text-ink text-sm'>{t('cookies.texto')}</p>
					<Link
						className='text-gray-ink text-sm underline hover:no-underline'
						params={{ documento: 'politica-de-cookies' }}
						to='/legal/$documento'
					>
						{t('cookies.verDetalle')}
					</Link>
				</div>
				<div className='flex shrink-0 gap-2'>
					<Button
						aria-labelledby={`${idTitulo} ${idBotonRechazar}`}
						id={idBotonRechazar}
						onClick={() => decidir('rechazado')}
						type='button'
						variant='secondary'
					>
						{t('cookies.rechazar')}
					</Button>
					<Button
						aria-labelledby={`${idTitulo} ${idBotonAceptar}`}
						id={idBotonAceptar}
						onClick={() => decidir('aceptado')}
						type='button'
						variant='accent'
					>
						{t('cookies.aceptar')}
					</Button>
				</div>
			</section>
		</div>
	);
};
